import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { SubscriptionContext } from "../../context/SubscriptionContext";
import api from "../../utils/Api";
import { Users, Clock, AlertCircle, TrendingUp, Zap } from "lucide-react";

const estadoBadge = {
  pendiente: "tn-badge tn-badge--yellow",
  resuelta:  "tn-badge tn-badge--green",
  rechazada: "tn-badge tn-badge--red",
};

export default function DashboardAdmin() {
  const { token, loading } = useContext(AuthContext);
  const { subscribed } = useContext(SubscriptionContext);
  const [resumen, setResumen] = useState({ fichajesHoy: 0, incidenciasPendientes: 0, totalEmpleados: 0, ultimaIncidencia: null });
  const [incidencias, setIncidencias] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [subInfo, setSubInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const loadData = async () => {
    if (!token) return;
    setLoadError(false);
    try {
      const hoy = new Date().toISOString().slice(0, 10);
      const [fichajesRes, incidenciasRes, empleadosRes, subRes] = await Promise.all([
        api.get("/fichajes", { params: { fecha_inicio: hoy } }),
        api.get("/incidencias"),
        api.get("/usuarios"),
        api.get("/subscribe/check", { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const fichajesArray    = Array.isArray(fichajesRes.data.data) ? fichajesRes.data.data : [];
      const incidenciasArray = Array.isArray(incidenciasRes.data)   ? incidenciasRes.data   : [];
      const empleadosArray   = Array.isArray(empleadosRes.data)     ? empleadosRes.data     : [];
      const pendientes = incidenciasArray.filter(i => i.estado === "pendiente");
      const ultima = [...incidenciasArray].sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora))[0] || null;
      setResumen({ fichajesHoy: fichajesArray.length, incidenciasPendientes: pendientes.length, totalEmpleados: empleadosArray.length, ultimaIncidencia: ultima });
      setIncidencias(incidenciasArray);
      setSubInfo(subRes.data);
    } catch { setLoadError(true); }
  };

  useEffect(() => { loadData(); }, [token]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>
      Cargando…
    </div>
  );

  const incidenciasFiltradas = filterEstado ? incidencias.filter(i => i.estado === filterEstado) : incidencias;

  const kpis = [
    { label: "Empleados",       value: resumen.totalEmpleados,         icon: <Users size={20} color="#FF7A00" />, bg: "var(--clr-accent-bg)" },
    { label: "Fichajes hoy",    value: resumen.fichajesHoy,            icon: <Clock size={20} color="#3B82F6" />, bg: "#EFF6FF" },
    { label: "Incid. pendient.", value: resumen.incidenciasPendientes, icon: <AlertCircle size={20} color="#F59E0B" />, bg: "#FFFBEB" },
    { label: "Actividad",       value: incidencias.length + " total",  icon: <TrendingUp size={20} color="#10B981" />, bg: "#F0FDF4" },
  ];

  const getSubscriptionMessage = () => {
    if (!subInfo) return null;
    const { status, on_trial, trial_ends_at, trial_days_left, renews_at, on_grace, ends_at } = subInfo;

    if (on_trial) {
      return `Período de prueba activo hasta ${trial_ends_at} (${trial_days_left} día${trial_days_left !== 1 ? "s" : ""} restante${trial_days_left !== 1 ? "s" : ""})`;
    }
    if (on_grace && ends_at) {
      return `⚠️ Acceso hasta ${ends_at}`;
    }
    if (status === "canceled") {
      return "❌ Suscripción cancelada";
    }
    if (renews_at) {
      return `Suscripción activa, se renueva el ${renews_at}`;
    }
    return null;
  };

  return (
    <div>
      <h1 className="tn-page-title">Dashboard</h1>
      {loadError && (
        <div style={{ padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, marginBottom: 16, fontSize: 14, color: "#B91C1C" }}>
          Error al cargar los datos del dashboard. Comprueba tu conexión y recarga la página.
        </div>
      )}
      {subInfo && getSubscriptionMessage() && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(255,122,0,.06)", border: "1px solid rgba(255,122,0,.18)", borderRadius: 8, marginBottom: 16, fontSize: 14, color: "var(--clr-text)" }}>
          <Zap size={16} style={{ color: "var(--clr-accent)" }} />
          {getSubscriptionMessage()}
        </div>
      )}
      <p className="tn-page-sub">Resumen del día — {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}</p>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 14, marginBottom: 28 }}>
        {kpis.map((k, i) => (
          <div key={i} className="tn-kpi">
            <div className="tn-kpi__icon" style={{ background: k.bg }}>{k.icon}</div>
            <div>
              <div className="tn-kpi__value">{k.value}</div>
              <div className="tn-kpi__label">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Incidencias recientes */}
      <div className="tn-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: "1px solid var(--clr-border)" }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "var(--clr-text)" }}>Incidencias recientes</span>
          <select
            className="tn-input tn-select"
            style={{ height: 34, width: "auto", fontSize: 13, padding: "0 32px 0 10px" }}
            value={filterEstado}
            onChange={e => setFilterEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="resuelta">Resuelta</option>
            <option value="rechazada">Rechazada</option>
          </select>
        </div>

        {incidenciasFiltradas.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14, padding: 20 }}>
            {incidenciasFiltradas.slice(0, 9).map(i => (
              <div key={i.id} className="tn-card tn-card--hover" style={{ padding: "16px", border: "1px solid var(--clr-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: "var(--clr-muted)" }}>{i.tipo}</span>
                  <span className={estadoBadge[i.estado] || "tn-badge tn-badge--slate"}>{i.estado}</span>
                </div>
                <p style={{ fontSize: 13.5, fontWeight: 600, color: "var(--clr-text)", margin: "0 0 4px" }}>{i.usuario?.nombre || "—"}</p>
                <p style={{ fontSize: 13, color: "var(--clr-muted)", margin: "0 0 8px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{i.comentario}</p>
                <p style={{ fontSize: 11.5, color: "var(--clr-muted-lt)", margin: 0 }}>{new Date(i.fecha_hora).toLocaleString("es-ES")}</p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>
            No hay incidencias para mostrar
          </div>
        )}
      </div>
    </div>
  );
}
